import { useEffect, useCallback, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { CommentableParagraphNode } from '../../nodes/CommentableParagraphNode';
import { CommentableHeadingNode } from '../../nodes/CommentableHeadingNode';
import { CommentableListNode } from '../../nodes/CommentableListNode';
import { $getNodeByKey, $nodesOfType } from 'lexical';
import { getListReactOfParagraph } from '../../../../apis/article.api';

type CommentEventDetail = {
  nodeId: string;
  articleId: string;
};

const CommentsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const lastProcessedEvent = useRef<string>('');

  const handleCommentPosted = useCallback(
    async (e: CustomEvent<CommentEventDetail>) => {
      const { nodeId, articleId } = e.detail;
      // Prevent duplicate processing of the same event
      const eventKey = `${nodeId}-${Date.now()}`;
      if (lastProcessedEvent.current === eventKey) {
        return;
      }
      lastProcessedEvent.current = eventKey;

      try {
        // Fetch the actual current comment count from the backend
        const currentStats = await getListReactOfParagraph(nodeId);

        // Find the specific node and update it only once
        editor.update(() => {
          let nodeFound = false;

          // Check paragraph nodes first
          const allNodes = $nodesOfType(CommentableParagraphNode);

          for (const node of allNodes) {
            if (node.__nodeId === nodeId && !nodeFound) {
              nodeFound = true;

              const currentData = node.getNodeData();

              const updatedNodeData = {
                ...currentData,
                comment: {
                  ...currentData.comment,
                  commentCount: currentStats.comment, // Use actual backend count
                  isCommented: true
                },
                timestamp: Date.now() // Add timestamp for change detection
              };

              node.setParagraph(updatedNodeData);
              break; // Exit loop after finding the first match
            }
          }

          // Only check other node types if not found in paragraphs
          if (!nodeFound) {
            const allHeadingNodes = $nodesOfType(CommentableHeadingNode);
            for (const node of allHeadingNodes) {
              if (node.__nodeId === nodeId) {
                const currentData = node.getNodeData();

                const updatedNodeData = {
                  ...currentData,
                  comment: {
                    ...currentData.comment,
                    commentCount: currentStats.comment,
                    isCommented: true
                  },
                  timestamp: Date.now()
                };

                node.setNodeData(updatedNodeData);
                break;
              }
            }
          }

          // Only check list nodes if still not found
          if (!nodeFound) {
            const allListNodes = $nodesOfType(CommentableListNode);
            for (const node of allListNodes) {
              if (node.__nodeId === nodeId) {
                const currentData = node.getNodeData();

                const updatedNodeData = {
                  ...currentData,
                  comment: {
                    ...currentData.comment,
                    commentCount: currentStats.comment,
                    isCommented: true
                  },
                  timestamp: Date.now()
                };

                node.setNodeData(updatedNodeData);
                break;
              }
            }
          }
        });

        // Dispatch the event after the editor update completes
        window.dispatchEvent(
          new CustomEvent('commentCountUpdated', {
            detail: {
              nodeId,
              newCommentCount: currentStats.comment
            }
          })
        );
      } catch (error) {
        console.error('Error updating comment count:', error);
      }
    },
    [editor]
  );

  useEffect(() => {
    // Listen for commentPosted events
    window.addEventListener('commentPosted', handleCommentPosted as EventListener);

    return () => {
      window.removeEventListener('commentPosted', handleCommentPosted as EventListener);
    };
  }, [handleCommentPosted]);

  return null;
};

export default CommentsPlugin;
